import { useState } from "react";
import "./ChangePassword.css";
import axios from "axios";
import { notificationActions } from "../../data/notification";
import { useDispatch, useSelector } from "react-redux";
import useT from "../../i18n/useT";
import { Eye, EyeSlash, ShieldLock, CheckCircleFill, XCircleFill } from "react-bootstrap-icons";

const scorePassword = (pw) => {
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s; // 0..4
};

const PasswordField = ({ label, value, onChange, show, onToggle, placeholder }) => (
    <div className="cp-field">
        <label>{label}</label>
        <div className="cp-input">
            <input required value={value} onChange={onChange} type={show ? "text" : "password"} placeholder={placeholder} className="form-control" />
            <button type="button" className="cp-eye" tabIndex={-1} onClick={onToggle} aria-label="Toggle password">
                {show ? <EyeSlash /> : <Eye />}
            </button>
        </div>
    </div>
);

const ChangePassword = () => {
    const [showPassword, setShowPassword] = useState({ old: false, new: false, repeat: false });
    const [formData, setFormData] = useState({ password: "", old_password: "", repeat_password: "" });
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const lang = useSelector((state) => state.user.language);
    const t = useT();
    const strengthLabels = ["", t("cp.weak"), t("cp.fair"), t("cp.good"), t("cp.strong")];

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    const score = scorePassword(formData.password);
    const match = formData.repeat_password.length > 0 && formData.password === formData.repeat_password;
    const canSubmit = formData.old_password && formData.password && match && !loading;

    const changePassword = (e) => {
        e.preventDefault();
        if (!match) { notify("New passwords do not match", "error-notification"); return; }
        setLoading(true);
        axios.put("/api/change-password", formData)
            .then((res) => {
                setLoading(false);
                notify(res.data.message[lang], "success-notification");
                setFormData({ password: "", old_password: "", repeat_password: "" });
            })
            .catch((err) => {
                setLoading(false);
                notify(err.response?.data?.message?.[lang] || "Something went wrong", "error-notification");
            });
    };

    return (
        <div className="cp-page">
            <div className="cp-card">
                <div className="cp-icon"><ShieldLock /></div>
                <h2 className="cp-title">{t("cp.title")}</h2>
                <p className="cp-sub">{t("cp.subtitle")}</p>

                <form onSubmit={changePassword} className="cp-form">
                    <PasswordField
                        label={t("cp.currentPassword")}
                        placeholder={t("cp.currentPlaceholder")}
                        value={formData.old_password}
                        onChange={(e) => setFormData((p) => ({ ...p, old_password: e.target.value }))}
                        show={showPassword.old}
                        onToggle={() => setShowPassword((p) => ({ ...p, old: !p.old }))}
                    />
                    <PasswordField
                        label={t("cp.newPassword")}
                        placeholder={t("cp.newPlaceholder")}
                        value={formData.password}
                        onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                        show={showPassword.new}
                        onToggle={() => setShowPassword((p) => ({ ...p, new: !p.new }))}
                    />
                    {formData.password && (
                        <div className={`cp-strength s${score}`}>
                            <div className="cp-bars">{[1, 2, 3, 4].map((i) => <span key={i} className={i <= score ? "on" : ""} />)}</div>
                            <span className="cp-strength-label">{strengthLabels[score]}</span>
                        </div>
                    )}
                    <PasswordField
                        label={t("cp.confirmPassword")}
                        placeholder={t("cp.confirmPlaceholder")}
                        value={formData.repeat_password}
                        onChange={(e) => setFormData((p) => ({ ...p, repeat_password: e.target.value }))}
                        show={showPassword.repeat}
                        onToggle={() => setShowPassword((p) => ({ ...p, repeat: !p.repeat }))}
                    />
                    {formData.repeat_password.length > 0 && (
                        <div className={`cp-match ${match ? "ok" : "bad"}`}>
                            {match ? <><CheckCircleFill /> {t("cp.match")}</> : <><XCircleFill /> {t("cp.noMatch")}</>}
                        </div>
                    )}

                    <button type="submit" disabled={!canSubmit} className="btn btn-primary cp-submit">
                        {loading ? <div className="spinner-border spinner-border-sm text-light" role="status"></div> : t("cp.update")}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ChangePassword;
